// Drawola: picks the app language. Fork-only.
//
// One pure function for the whole page: the inline welcome script in
// index.html runs its source text before first paint, and the React app calls
// it on load, so both always show the same language.

/** localStorage key of a manual language choice; the old detector cache key. */
export const LANGUAGE_STORAGE_KEY = "i18nextLng";

/**
 * Resolves the app language: a valid saved manual choice, then the first
 * supported browser language in order, then English.
 *
 * Must stay self-contained (no outer references, no syntax newer than the
 * browsers the app targets), because index.html embeds its source text.
 * A base language with several supported regions uses the CLDR likely region
 * (`zh` is Simplified, `pt` is Brazil); a base with no such default is skipped
 * rather than matched to whichever region sorts first.
 */
export function resolveAppLanguage(
  stored: string | null | undefined,
  browser: readonly string[],
  supported: readonly string[],
): string {
  const byKey: Record<string, string> = {};
  for (const code of supported) {
    byKey[code.toLowerCase()] = code;
  }
  const BASE_DEFAULTS: Record<string, string> = { pt: "pt-br", zh: "zh-cn" };
  const TRADITIONAL_REGIONS = ["tw", "hk", "mo"];

  /** A supported code by lowercase key; own keys only, so `constructor` misses. */
  function lookup(key: string): string {
    return Object.prototype.hasOwnProperty.call(byKey, key) ? byKey[key] : "";
  }

  /** Lowercase tag with `-` separators, or "" for a non-string. */
  function normalize(tag: unknown): string {
    return typeof tag === "string"
      ? tag.trim().replace(/_/g, "-").toLowerCase()
      : "";
  }

  /** Script first, then region: the Chinese key a tag's parts point to. */
  function chinese(parts: string[]): string {
    const script = parts.find((part) => part.length === 4);
    if (script === "hant") {
      return "zh-tw";
    }
    if (script === "hans") {
      return "zh-cn";
    }
    const region = parts.find((part) => part.length === 2 && part !== "zh");
    return region && TRADITIONAL_REGIONS.indexOf(region) !== -1
      ? "zh-tw"
      : "zh-cn";
  }

  /** The supported code for one browser tag, or null when none fits. */
  function match(tag: unknown): string | null {
    const key = normalize(tag);
    if (!key) {
      return null;
    }
    if (lookup(key)) {
      return lookup(key);
    }
    const parts = key.split("-");
    const base = parts[0];
    if (base === "zh") {
      return lookup(chinese(parts)) || null;
    }
    const regions = Object.keys(byKey).filter(
      (code) => code === base || code.split("-")[0] === base,
    );
    if (regions.length === 1) {
      return byKey[regions[0]];
    }
    const preferred = Object.prototype.hasOwnProperty.call(BASE_DEFAULTS, base)
      ? BASE_DEFAULTS[base]
      : "";
    return lookup(preferred) || null;
  }

  const saved = lookup(normalize(stored));
  if (saved) {
    return saved;
  }
  for (const tag of browser) {
    const found = match(tag);
    if (found) {
      return found;
    }
  }
  // English when nothing matched; a literal, as the source must stand alone
  return lookup("en") || "en";
}
